import cn from 'clsx';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { ClickedProps, ValueProp, isLinkProps, isAnchorProps, isButtonProps, isDivProps } from './types';

import s from './BaseClickable.module.scss';

export type BaseClickableProps<V extends ValueProp> = {
  className?: string;
  block?: boolean;
  withHover?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  ariaLabel?: string;
  style?: React.CSSProperties;
} & ClickedProps<V>;

const BaseClickable = <V extends ValueProp>(
  {
    children = null,
    className,
    value,
    block,
    onClickWithValue,
    onClick,
    withHover,
    disabled,
    ariaLabel,
    ...props
  }: BaseClickableProps<V>,
  ref: React.ForwardedRef<any>,
): React.ReactElement | null => {
  const baseProps = { ...props }; // Копируем, чтобы не мутировать props

  baseProps.element ??= 'button'; // element = button - по умолчанию (нельзя вытаскивать из props из-за typeguard'ов)

  const handlerClick = (e: React.MouseEvent) => {
    onClickWithValue?.(value as V);
    onClick?.(e);
  };

  const commonClassName = cn(
    s.base,
    block && s.base_block,
    withHover && s['base_with-hover'],
    disabled && s.base_disabled,
    className,
  );

  const commonProps = {
    style: props.style,
    className: commonClassName,
    onClick: handlerClick,
    'aria-label': ariaLabel,
    element: undefined,
  };

  if (isLinkProps(baseProps)) {
    return (
      <Link {...baseProps} {...commonProps} ref={ref}>
        {children}
      </Link>
    );
  }

  if (isAnchorProps(baseProps)) {
    return (
      <a rel="noopener noreferrer nofollow" target="_blank" {...baseProps} {...commonProps} ref={ref}>
        {children}
      </a>
    );
  }

  if (isButtonProps(baseProps)) {
    return (
      <button
        // @ts-ignore
        type="button"
        {...baseProps}
        {...commonProps}
        ref={ref}
        disabled={disabled}
      >
        {children}
      </button>
    );
  }

  if (isDivProps(baseProps)) {
    return (
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            handlerClick(e as unknown as React.MouseEvent);
          }
        }}
        {...baseProps}
        {...commonProps}
        ref={ref}
        aria-disabled={disabled}
      >
        {children}
      </div>
    );
  }

  return null;
};

export default React.forwardRef(BaseClickable) as typeof BaseClickable;
